import React, {useState, useCallback, useEffect, useRef} from 'react';
import {APIProvider, Map, AdvancedMarker, useMapsLibrary, useMap, InfoWindow} from '@vis.gl/react-google-maps';
import {Box, TextField, Typography, Link, Button} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
// import EmailIcon from '@mui/icons-material/Email';
import {LocationStickyProps} from '../../pages/OurSchools';
import prodigyOwl from '../prodigy-owl.svg';
import microVpkOwl from '../micro-vpk-owl.svg';
import './SchoolMap.css';

interface SchoolMapProps {
    schools: LocationStickyProps[];
}

interface SchoolWithCoordinates extends LocationStickyProps {
    lat: number;
    lng: number;
}

// Address input component with autocomplete
const AddressAutocomplete: React.FC<{
    onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
}> = ({onPlaceSelect}) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const places = useMapsLibrary('places');
    const map = useMap();
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
    const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

    useEffect(() => {
        if (!places || !inputRef.current) return;

        const autocomplete = new places.Autocomplete(inputRef.current, {
            fields: ['geometry', 'formatted_address'],
            componentRestrictions: {country: 'us'}
        });

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.geometry?.location) {
                setInputValue(place.formatted_address || '');
                onPlaceSelect(place);
            }
        });

        autocompleteRef.current = autocomplete;

        // Initialize autocomplete service for manual predictions
        if (!autocompleteServiceRef.current) {
            autocompleteServiceRef.current = new places.AutocompleteService();
        }

        // Initialize places service for getting place details
        if (!placesServiceRef.current && inputRef.current) {
            const dummyDiv = document.createElement('div');
            placesServiceRef.current = new places.PlacesService(dummyDiv);
        }
    }, [places, onPlaceSelect, map]);

    // Handle Enter key to auto-select first address
    const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && inputValue && autocompleteServiceRef.current && placesServiceRef.current) {
            e.preventDefault();

            // Get predictions
            autocompleteServiceRef.current.getPlacePredictions(
                {
                    input: inputValue,
                    componentRestrictions: {country: 'us'}
                },
                (predictions, status) => {
                    if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions[0]) {
                        // Get details for the first prediction
                        const firstPrediction = predictions[0];

                        if (placesServiceRef.current) {
                            placesServiceRef.current.getDetails(
                                {
                                    placeId: firstPrediction.place_id,
                                    fields: ['geometry', 'formatted_address']
                                },
                                (place, detailsStatus) => {
                                    if (detailsStatus === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                                        setInputValue(place.formatted_address || firstPrediction.description);
                                        onPlaceSelect(place);
                                    }
                                }
                            );
                        }
                    }
                }
            );
        }
    };

    return (
        <TextField
            inputRef={inputRef}
            fullWidth
            label="Enter your address"
            variant="outlined"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="e.g., 123 Main St, Orlando, FL 32801"
            sx={{
                backgroundColor: 'transparent',
                '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: 'white',
                }
            }}
        />
    );
};

const SchoolMap: React.FC<SchoolMapProps> = ({schools}) => {
    const [schoolsWithCoords, setSchoolsWithCoords] = useState<SchoolWithCoordinates[]>([]);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
    const [selectedSchool, setSelectedSchool] = useState<SchoolWithCoordinates | null>(null);

    // Use cached coordinates from school data
    useEffect(() => {
        const schoolsWithCoordinates: SchoolWithCoordinates[] = schools
            .filter(school => school.latitude !== undefined && school.longitude !== undefined)
            .map(school => ({
                ...school,
                lat: school.latitude!,
                lng: school.longitude!
            }));

        setSchoolsWithCoords(schoolsWithCoordinates);
    }, [schools]);

    const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
        if (!mapInstance || !place.geometry?.location) return;

        const location = place.geometry.location;
        mapInstance.panTo(location);
        mapInstance.setZoom(10); // Zoom level that roughly shows 25-mile radius
    }, [mapInstance]);

    // Component to handle map instance
    const MapContent = () => {
        const map = useMap();

        useEffect(() => {
            if (map) {
                setMapInstance(map);
            }
        }, [map]);

        return null;
    };

    return (
        <Box sx={{width: '100%', mb: 4}}>
            <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                {/* Address Input Section */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                    flexDirection: {xs: 'column', sm: 'row'},
                    alignItems: {xs: 'stretch', sm: 'center'}
                }}>
                    <AddressAutocomplete onPlaceSelect={handlePlaceSelect}/>
                </Box>

                {/* Map Container */}
                <Box sx={{
                    width: '100%',
                    height: '500px',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    boxShadow: 3
                }}>
                    <Map
                        defaultCenter={{lat: 28.5383, lng: -81.3792}}
                        defaultZoom={7}
                        mapId="prodigy-schools-map"
                        gestureHandling="greedy"
                        disableDefaultUI={false}
                    >
                        <MapContent/>
                        {/* Render micro schools first, then Prodigy schools on top */}
                        {[...schoolsWithCoords]
                            .sort((a, b) => (a.microSchool ? -1 : 1) - (b.microSchool ? -1 : 1))
                            .map((school, index) => (
                                <AdvancedMarker
                                    key={index}
                                    position={{lat: school.lat, lng: school.lng}}
                                    title={school.name}
                                    onClick={() => setSelectedSchool(school)}
                                    zIndex={school.microSchool ? 1 : 2}
                                >
                                    <Box
                                        component="img"
                                        src={school.microSchool ? microVpkOwl : prodigyOwl}
                                        alt={school.name}
                                        sx={{
                                            width: '48px',
                                            height: '48px',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                transform: 'scale(1.1)',
                                                transition: 'transform 0.2s'
                                            }
                                        }}
                                    />
                                </AdvancedMarker>
                            ))}

                        {selectedSchool && (
                            <InfoWindow
                                position={{lat: selectedSchool.lat, lng: selectedSchool.lng}}
                                onCloseClick={() => setSelectedSchool(null)}
                            >
                                <Box sx={{
                                    p: 2,
                                    width: '300px',
                                    m: 'auto',
                                }}>
                                    {/* School Photo */}
                                    {selectedSchool.photo && (
                                        <Box sx={{
                                            width: '250px',
                                            height: '100px',
                                            mb: 2,
                                            borderRadius: 2,
                                            m: 'auto',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <img
                                                src={selectedSchool.photo}
                                                alt={selectedSchool.name}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block'
                                                }}
                                            />
                                        </Box>
                                    )}

                                    <Typography variant="h6" fontWeight="bold" sx={{mb: 2, textAlign: 'center'}}>
                                        {selectedSchool.name}
                                    </Typography>

                                    {/* Address */}
                                    {(selectedSchool.address1 || selectedSchool.address2) && (
                                        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1}}>
                                            <LocationOnIcon sx={{fontSize: 20, color: 'primary.main', mt: 0.2}}/>
                                            <Box>
                                                {selectedSchool.address1 && (
                                                    <Typography variant="body2"
                                                                sx={{lineHeight: 1.4, textAlign: 'left'}}>
                                                        {selectedSchool.address1}
                                                    </Typography>
                                                )}
                                                {selectedSchool.address2 && (
                                                    <Typography variant="body2"
                                                                sx={{lineHeight: 1.4, textAlign: 'left'}}>
                                                        {selectedSchool.address2}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Phone */}
                                    {selectedSchool.phone && selectedSchool.phone.trim() !== '' && (
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                                            <PhoneIcon sx={{fontSize: 20, color: 'primary.main'}}/>
                                            <Typography variant="body2" fontWeight="bold">
                                                <Link href={`tel:${selectedSchool.phone}`}
                                                      sx={{color: 'primary.main', textDecoration: 'none'}}>
                                                    {selectedSchool.phone}
                                                </Link>
                                            </Typography>
                                        </Box>
                                    )}

                                    {/*{selectedSchool.email && selectedSchool.email.trim() !== '' && (*/}
                                    {/*    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>*/}
                                    {/*        <EmailIcon sx={{ fontSize: 20, color: 'primary.main' }} />*/}
                                    {/*        <Typography variant="body2" fontWeight="bold">*/}
                                    {/*            <Link href={`mailto:${selectedSchool.email}`} sx={{ color: 'primary.main', textDecoration: 'none' }}>*/}
                                    {/*                {selectedSchool.email}*/}
                                    {/*            </Link>*/}
                                    {/*        </Typography>*/}
                                    {/*    </Box>*/}
                                    {/*)}*/}

                                    {/* Contact Us Button */}
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        href={`/lets-talk?school=${selectedSchool.schoolKey}`}
                                        sx={{
                                            mt: 1,
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Contact Us
                                    </Button>
                                </Box>
                            </InfoWindow>
                        )}
                    </Map>
                </Box>

                {/* Legend */}
                <Box sx={{
                    display: 'flex',
                    gap: 3,
                    mt: 2,
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#3a1f0b',
                            border: '2px solid #211106'
                        }}/>
                        <Typography variant="body2" fontWeight="bold">Prodigy</Typography>
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Box sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: '#f0e4d8',
                            border: '2px solid #b6aca2'
                        }}/>
                        <Typography variant="body2" fontWeight="bold">Florida CSUSA PK</Typography>
                    </Box>
                </Box>
            </APIProvider>
        </Box>
    );
};

export default SchoolMap;